import { ProductNames } from "constants/forecasts.constants";
import { TYPE_WELLS } from "constants/settings.constants";
import { ICheckedForecast } from "store/features";
import {
  PRODUCTION_SUMMARY_PROPERTY_PREFIX,
  isNormalizedProductionSummaryProperty
} from "utils/column/isNormalizedProductionSummary";

import { WellData } from "models/UserArpsModel";

import { getTitle } from "components/arps/utils/getTitle";

/**
 * Mapping from API field names to wellData property names.
 * Used as a fallback when the API returns null for a field.
 */
export const FIELD_TO_WELLDATA_MAP: Record<string, keyof WellData> = {
  "Well_Design.HzLength_m": "HzLength",
  "Well_Design.HzLengthSimple_m": "HzLength",
  "Well_Design.HzLengthDrilled_m": "HzLength",
  "Well_Design.HzLengthCompleted_m": "HzLength",
  "Frac_Data.StagesActual": "Stage",
  // EVA-6384: "Stages" must fall back to the same computed Stage value as "Stages Actual"
  "Frac_Data.Stages": "Stage",
  "Frac_Data.ProppantTotal_t": "Proppant",
  "Frac_Data.StageSpacing_m": "StageSpacing",
  "Frac_Data.ProppantIntensity_tm": "ProppantIntensity"
};

/**
 * Fields that should NOT be normalized (design fields, dates, ratios).
 * These are either the normalization denominator themselves or non-production values.
 */
const NON_NORMALIZABLE_FIELD_PATTERNS = [
  "Dates.",
  "Well_Design.",
  "Frac_Data.",
  "Header.",
  "_GOR",
  "_WOR",
  "_CGR",
  "_WGR",
  "_WCut",
  "_OSR",
  "_Yield",
  ".GOR",
  ".WOR",
  ".CGR",
  ".WGR",
  ".WCut",
  ".OSR",
  ".Yield"
];

/**
 * Check if a field should be normalized.
 * Returns false for dates, design fields, and ratio products.
 */
export function isNormalizableField(field: string): boolean {
  if (field.startsWith(PRODUCTION_SUMMARY_PROPERTY_PREFIX)) {
    return isNormalizedProductionSummaryProperty(field);
  }
  return !NON_NORMALIZABLE_FIELD_PATTERNS.some(
    (pattern) => field.includes(pattern) || field.startsWith(pattern)
  );
}

/**
 * Get a value from type well's wellData for a given API field.
 * Returns null if the field doesn't map to wellData or the value is missing.
 */
export function getWellDataValueForField(
  tw: ICheckedForecast,
  field: string
): number | null {
  const wellDataKey = FIELD_TO_WELLDATA_MAP[field];
  if (!wellDataKey) {
    // Check if it's in AdditionalData
    const additionalData = tw.wellData?.AdditionalData?.find((ad) => ad.field === field);
    return additionalData?.value ?? null;
  }

  const value = tw.wellData?.[wellDataKey];
  return typeof value === "number" && value > 0 ? value : null;
}

/**
 * Format a date string based on the expected format for the field type.
 * - Fields ending in "_Year" → "YYYY"
 * - Fields ending in "_YearQtr" → "YYYY-MM-01" (first month of quarter)
 * - Fields ending in "_YearMonth" → "YYYY-MM"
 * - Other date fields → "YYYY-MM-DD"
 *
 * Uses UTC methods to avoid timezone-related date shifts.
 */
export function formatDateForField(dateString: string, property: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  if (property.endsWith("_YearQtr")) {
    const quarterMonth = Math.floor(date.getUTCMonth() / 3) * 3 + 1;
    const qMonth = String(quarterMonth).padStart(2, "0");
    return `${year}-${qMonth}-01`;
  } else if (property.endsWith("_YearMonth")) {
    // Year-Month fields: "YYYY-MM"
    return `${year}-${month}`;
  } else if (property.endsWith("_Year")) {
    // Year only fields: "YYYY"
    return `${year}`;
  } else {
    // Full date fields: "YYYY-MM-DD"
    return `${year}-${month}-${day}`;
  }
}

/**
 * Unit conversion functions for wellData values.
 * These convert from source units (e.g., meters) to display units (e.g., feet).
 */
export interface WellDataUnitConversions {
  convertLength?: (value: number) => number;
  convertProppant?: (value: number) => number;
  convertProppantIntensity?: (value: number) => number;
}

/**
 * Convert a wellData value to display units based on the field type.
 * Only applies to wellData fallback values (API returns already-converted values).
 */
export function convertWellDataValue(
  value: number,
  property: string,
  unitConversions?: WellDataUnitConversions
): number {
  if (!unitConversions) return value;

  // Length fields (HzLength variants, StageSpacing)
  if (property.includes("HzLength") || property.includes("StageSpacing")) {
    return unitConversions.convertLength?.(value) ?? value;
  }

  // Proppant intensity (t/m → display unit)
  if (property.includes("ProppantIntensity")) {
    return unitConversions.convertProppantIntensity?.(value) ?? value;
  }

  // Proppant total (t → display unit)
  if (property.includes("ProppantTotal")) {
    return unitConversions.convertProppant?.(value) ?? value;
  }

  return value;
}

/**
 * Get the value for a cross plot field from a type well.
 * Priority: 1) Dates from arps, 2) API performance data, 3) wellData fallback (with unit conversion)
 */
export function getCrossPlotFieldValue(
  tw: ICheckedForecast,
  property: string,
  perfData: Record<string, number | null> | undefined,
  unitConversions?: WellDataUnitConversions
): number | string | null {
  // For fields starting with "Dates.", use the type well's primary product start date
  if (property.startsWith("Dates.")) {
    const primaryArps = tw.arps?.[0];
    if (!primaryArps?.startDate) return null;

    const formattedDate = formatDateForField(primaryArps.startDate, property);

    // For _Year fields, return as number since the x-axis is numeric
    if (property.endsWith("_Year")) {
      const yearNum = parseInt(formattedDate, 10);
      return isNaN(yearNum) ? null : yearNum;
    }

    return formattedDate;
  }

  // Try API data first (already in display units)
  const apiValue = perfData?.[property];
  if (apiValue != null) {
    return apiValue;
  }

  // Fall back to wellData if API returned null
  // wellData is stored in source units, so convert to display units
  const wellDataValue = getWellDataValueForField(tw, property);
  if (wellDataValue == null) return null;

  return convertWellDataValue(wellDataValue, property, unitConversions);
}

// ============================================================================
// Normalization Utilities (shared by Cross Plot and Box Plot)
// ============================================================================

export interface CrossPlotNormalizationSettings {
  enabled: boolean;
  normalizeTypeWell: boolean;
  field: string;
  per: number;
  threshold: number;
  useMultilinearNormalization: boolean;
  higherScalar: number;
}

/**
 * Check if a type well can be normalized (has the required well data value).
 * Returns the well value if available, or null if missing.
 */
export function canNormalizeTypeWell(
  tw: ICheckedForecast,
  normalization: CrossPlotNormalizationSettings
): { canNormalize: boolean; wellValue: number | null } {
  if (
    !normalization.enabled ||
    !normalization.normalizeTypeWell ||
    normalization.per === 0
  ) {
    // Normalization not active, no check needed
    return { canNormalize: true, wellValue: null };
  }

  const wellValue = getWellDataValueForField(tw, normalization.field);
  if (wellValue == null || wellValue <= 0) {
    return { canNormalize: false, wellValue: null };
  }

  return { canNormalize: true, wellValue };
}

/**
 * Calculate the normalization multiplier for a type well.
 * Assumes the well value has already been validated.
 */
export function calculateNormalizationMultiplier(
  wellValue: number,
  normalization: CrossPlotNormalizationSettings
): number {
  const { per, useMultilinearNormalization, threshold, higherScalar } = normalization;

  if (!useMultilinearNormalization) {
    // Simple linear normalization: per / wellValue
    return 1.0 / (wellValue / per);
  }

  // Multilinear normalization
  return calculateMultilinearNormalization(wellValue, threshold, higherScalar, per);
}

/**
 * Calculate multilinear normalization multiplier.
 * Handles non-linear scaling around a threshold.
 */
function calculateMultilinearNormalization(
  wellValue: number,
  threshold: number,
  scaleAfterThreshold: number,
  per: number
): number {
  const val =
    threshold > per
      ? normalizeByPerValue(wellValue, threshold, per, scaleAfterThreshold)
      : scaleUpToNormalizeByValue(wellValue, threshold, per, scaleAfterThreshold);
  return 1.0 / val;
}

function normalizeByPerValue(
  wellValue: number,
  threshold: number,
  per: number,
  scaleAfterThreshold: number
): number {
  if (threshold >= wellValue) {
    return wellValue / per;
  }
  const upToThreshold = threshold / per;
  const scaledRemainingAfterThreshold =
    ((wellValue - threshold) * (1.0 / scaleAfterThreshold)) / per;
  return upToThreshold + scaledRemainingAfterThreshold;
}

function scaleUpToNormalizeByValue(
  wellValue: number,
  threshold: number,
  per: number,
  scaleAfterThreshold: number
): number {
  if (threshold > wellValue) {
    return (
      1.0 /
      ((threshold / wellValue) *
        ((threshold + (per - threshold) * scaleAfterThreshold) / threshold))
    );
  } else {
    return 1.0 / ((wellValue + (per - wellValue) * scaleAfterThreshold) / wellValue);
  }
}

/**
 * Get display name for a type well.
 */
export function getTypeWellDisplayName(tw: ICheckedForecast): string {
  return getTitle(tw, TYPE_WELLS);
}

// ============================================================================
// Product Availability Utilities (for zero-value handling)
// ============================================================================

/**
 * Normalize a product name to uppercase standard format.
 * Handles various casing and naming conventions.
 */
function normalizeProductName(product: string): string {
  const upper = product.toUpperCase();

  // Map common variations
  if (
    upper === ProductNames.Condensate ||
    upper === ProductNames.CondAbbreviated ||
    upper === ProductNames.CondAbbreviatedWithDot
  ) {
    return ProductNames.Oil;
  }
  if (upper === ProductNames.SalesGas) {
    return ProductNames.SalesGas;
  }

  return upper;
}

/**
 * Mapping of ratio products to what they can derive.
 * Key: ratio constant product name (uppercase)
 * Value: { requires: base product needed, derives: product that can be calculated }
 */
const RATIO_DERIVATION_MAP: Record<
  string,
  { requires: string; derives: string } | { requires: string; derives: string }[]
> = {
  // OIL-based ratios
  [ProductNames.GOR]: { requires: ProductNames.Oil, derives: ProductNames.Gas },
  [ProductNames.WOR]: { requires: ProductNames.Oil, derives: ProductNames.Water },
  [ProductNames.OnePlusWOR]: { requires: ProductNames.Oil, derives: ProductNames.Water },
  [ProductNames.OilPlusWater]: {
    requires: ProductNames.Oil,
    derives: ProductNames.Water
  },
  [ProductNames.SOR]: { requires: ProductNames.Oil, derives: ProductNames.Steam },

  // GAS-based ratios
  [ProductNames.CGR]: [
    { requires: ProductNames.Gas, derives: ProductNames.Oil },
    { requires: ProductNames.Gas, derives: ProductNames.Condensate }
  ],
  [ProductNames.OGR]: { requires: ProductNames.Gas, derives: ProductNames.Oil },
  [ProductNames.WGR]: { requires: ProductNames.Gas, derives: ProductNames.Water }
};

/**
 * Get all products that a type well can produce values for.
 * Includes:
 * 1. Primary products directly from arps segments
 * 2. Derived products from ratio constants (e.g., OIL + GOR → GAS)
 */
export function getTypeWellAvailableProducts(tw: ICheckedForecast): Set<string> {
  const products = new Set<string>();

  // 1. Add products from arps segments
  if (tw.arps && Array.isArray(tw.arps)) {
    for (const segment of tw.arps) {
      if (segment.product) {
        products.add(normalizeProductName(segment.product));
      }
    }
  }

  // 2. Check constants for ratios that can derive additional products
  if (tw.constants && Array.isArray(tw.constants)) {
    for (const constant of tw.constants) {
      if (!constant.product) continue;

      const ratioProduct = normalizeProductName(constant.product);
      const derivation = RATIO_DERIVATION_MAP[ratioProduct];

      if (!derivation) continue;

      // Handle single derivation or array of derivations
      const derivations = Array.isArray(derivation) ? derivation : [derivation];

      for (const { requires, derives } of derivations) {
        // If the type well has the required base product, it can derive the secondary
        if (products.has(requires)) {
          products.add(derives);
        }
      }
    }
  }

  return products;
}

/**
 * Extract the product name from a performance field path.
 * E.g., "Performance.Gas.EUR" → "GAS"
 *       "Performance.Oil.TwelveMCalTimeCum" → "OIL"
 *       "Well_Design.HzLength" → null (not a performance field)
 */
export function getProductFromPerformanceField(field: string): string | null {
  if (!field.startsWith("Performance.")) return null;

  const parts = field.split(".");
  if (parts.length < 2) return null;

  const productPart = parts[1];
  // Normalize common variations
  return normalizeProductName(productPart);
}

/**
 * Determine if a zero value should be skipped for a type well.
 *
 * Logic:
 * - If value is not zero, never skip (return false)
 * - If the field is not a performance field, don't skip zeros (return false)
 * - If the product IS available in the type well, don't skip (true zero, return false)
 * - If the product is NOT available, skip it (return true)
 *
 * This handles cases like:
 * - TW with OIL + GAS: Gas EUR = 0 → plot it (true zero, e.g., flaring)
 * - TW with OIL only: Gas EUR = 0 → skip (product doesn't exist)
 * - TW with OIL + GOR: Gas EUR = 0 → plot it (gas is derivable from ratio)
 */
export function shouldSkipZeroValue(
  tw: ICheckedForecast,
  field: string,
  value: number | string | null
): boolean {
  if (value !== 0) return false;

  const product = getProductFromPerformanceField(field);

  // If we can't determine the product (non-performance field), don't skip
  if (!product) return false;

  const availableProducts = getTypeWellAvailableProducts(tw);

  if (availableProducts.has(product)) return false;

  // Product is NOT available - skip this zero value
  return true;
}
